/**
 * In-memory source of truth for the active building pointer (the building the
 * nav surfaces scope to when the URL carries no `/project/{id}`).
 *
 * Why not localStorage directly: some browser configurations (observed in Brave
 * with ephemeral/locked storage) make `localStorage` writes non-durable — a
 * `setItem` is visible to a same-tick `getItem` but a later read reverts to the
 * page-load snapshot. With the previous localStorage-round-trip design this made
 * the eager-persist effect write the URL building forever (the read never caught
 * up), pegging the CPU in an infinite loop, and on navigation to a secondary
 * page the reverted read reset the active building to `projects[0]`.
 *
 * Fix: the SESSION source of truth is this module-level singleton — it survives
 * SPA navigation (the module is not unmounted) and never reverts. localStorage
 * is a BEST-EFFORT mirror for cross-reload / cross-tab continuity only; if a
 * browser drops the write, in-memory still holds the correct value for the rest
 * of the session. Writes are idempotent (no-op + no notify when unchanged) which
 * is what structurally prevents the persist loop.
 *
 * The pointer is per-user: `current` carries the userId it belongs to, so an
 * account switch re-hydrates instead of leaking the previous user's building.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let current: { userId: string; projectId: string } | null = null;

function notify(): void {
  for (const listener of listeners) listener();
}

function lsKey(userId: string): string {
  return `${userId}:facade:lastOpenedProjectId`;
}

function readMirror(userId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(lsKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { projectId?: string } | null;
    return parsed?.projectId ?? null;
  } catch {
    return null;
  }
}

function writeMirror(userId: string, projectId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      lsKey(userId),
      JSON.stringify({ projectId, timestamp: Date.now() })
    );
    // Keep cross-tab/other-instance listeners in sync where storage IS durable.
    window.dispatchEvent(new Event('local-storage'));
  } catch {
    /* best-effort — in-memory `current` remains authoritative this session */
  }
}

function clearMirror(userId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(lsKey(userId));
    window.dispatchEvent(new Event('local-storage'));
  } catch {
    /* best-effort */
  }
}

export function subscribeActiveProject(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Stable snapshot for the given user. In-memory `current` wins; on a miss (cold
 * session or after an account switch) we lazily hydrate from the localStorage
 * mirror so a normal reload restores the last building. Returns a primitive, so
 * `useSyncExternalStore` sees a stable value across calls once settled.
 */
export function getActiveProjectSnapshot(userId: string): string | null {
  if (current && current.userId === userId) return current.projectId;
  const mirrored = readMirror(userId);
  current = mirrored ? { userId, projectId: mirrored } : null;
  return mirrored;
}

export function setActiveProject(userId: string, projectId: string): void {
  // Idempotent: same pointer → no state change, no notify. This is the guard
  // that breaks the persist loop when the localStorage mirror is non-durable.
  if (current && current.userId === userId && current.projectId === projectId) {
    return;
  }
  current = { userId, projectId };
  writeMirror(userId, projectId);
  notify();
}

export function clearActiveProject(userId: string): void {
  if (current && current.userId === userId) current = null;
  clearMirror(userId);
  notify();
}

/**
 * Drop the in-memory pointer if it points at `projectId` (any user) — called
 * when a building is deleted so the nav doesn't keep resolving a dead building.
 */
export function forgetActiveProjectIfMatches(projectId: string): void {
  if (current && current.projectId === projectId) {
    current = null;
    notify();
  }
}

/** Reset on logout so the next account on this device starts clean. */
export function resetActiveProjectStore(): void {
  current = null;
  notify();
}
